import { cn, Table } from "@kyan/ui";

import { useMarket } from "#/store/app/portfolio/market";
import { type OrderbookEntry } from "#/types/orderbook";

import { type LastTradesTableEntry } from "../LastTradesTable/LastTradesTable.types";
import { OrderbookTableRowEffect } from "./components/OrderbookTableRowEffect";
import { OrderbookTicker } from "./components/OrderbookTicker";
import { getColumns } from "./OrderbookTable.config";
import type { OrderbookTableVariant } from "./OrderbookTable.types";

const findMaxTotal = (values: OrderbookEntry[]) => {
  if (!values) return 0;
  return values.reduce((acc, { total }) => Math.max(acc, total), 0);
};

const updateDataWithEmptyLayers = (
  data: OrderbookEntry[],
  layerSize: number,
  isBuy: boolean,
): OrderbookEntry[] => {
  if (!data) return [];
  const missingLayerCount = layerSize - data.length;

  if (missingLayerCount <= 0) return data;

  const missingLayers = Array.from({ length: missingLayerCount }, () => ({
    price: NaN,
    size: NaN,
    total: NaN,
  }));

  return isBuy ? [...data, ...missingLayers] : [...missingLayers, ...data];
};

export interface OrderbookTableProps {
  variant: OrderbookTableVariant;
  buys: OrderbookEntry[];
  sells: OrderbookEntry[];
  lastTrade?: LastTradesTableEntry;
  price?: number;
  indexPrice: number;
  layerSize?: number;
  isOrderbookLoading?: boolean;
  isLastTradesLoading?: boolean;
  isPerpPage?: boolean;
  onSelectRow: (entry: OrderbookEntry, isBuy: boolean) => void;
}

export const OrderbookTable = ({
  variant,
  buys,
  sells,
  lastTrade,
  price,
  indexPrice,
  layerSize = 0,
  isOrderbookLoading = false,
  isLastTradesLoading = false,
  isPerpPage = true,
  onSelectRow,
}: OrderbookTableProps) => {
  const isOptions = variant === "options";
  const { market } = useMarket();

  const sellMaxTotal = findMaxTotal(sells);
  const buyMaxTotal = findMaxTotal(buys);
  const columns = getColumns(market, isOptions);
  const updatedSells = updateDataWithEmptyLayers(sells, layerSize, false);
  const updatedBuys = updateDataWithEmptyLayers(buys, layerSize, true);

  const renderSide = (isBuys: boolean) => {
    return (
      <Table
        columns={columns}
        hideHeader={isBuys}
        isLoading={isOrderbookLoading}
        data={isBuys ? updatedBuys : updatedSells}
        classes={{
          wrapper: "bg-transparent leading-0",
          table: "border-none align-bottom",
          thead: cn({
            "pl-1": !isPerpPage,
            "border-divider border-b": !isPerpPage || isOptions,
          }),
          row: ({ row }) => {
            return cn("mb-px border-none", {
              "even:bg-buttonBg dark:even:bg-buttonFill hover:bg-buttonHover cursor-pointer":
                !isNaN(row.price),
              "pl-1": !isPerpPage,
            });
          },
          columnClassName: "p-0",
          cellClassName: cn("h-[18px] p-0 leading-[18px]", {
            "text-error": !isBuys,
            "text-success": isBuys,
          }),
          loading: {
            wrapper: "p-1 border-t-0",
            skeleton: "h-2.5 mb-px",
          },
        }}
        onRowClick={({ row }: { row: OrderbookEntry }) => {
          if (isNaN(row.price)) return;

          onSelectRow(row, isBuys);
        }}
        renderRowEffect={({ row }: { row: OrderbookEntry }) => {
          if (isNaN(row.price)) return null;

          return (
            <OrderbookTableRowEffect
              isBuys={isBuys}
              total={row.total}
              maxTotal={isBuys ? buyMaxTotal : sellMaxTotal}
            />
          );
        }}
      />
    );
  };

  return (
    <div
      className={cn("flex flex-col gap-0", {
        "border-divider overflow-hidden rounded-b-md border": isOptions,
      })}
    >
      {renderSide(false)}

      <OrderbookTicker
        price={price}
        variant={variant}
        lastTrade={lastTrade}
        indexPrice={indexPrice}
        isPerpPage={isPerpPage}
        isLoading={isLastTradesLoading}
      />

      {renderSide(true)}
    </div>
  );
};
